import React, { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import {
  IFavoriteAnalysisMutation,
  IFavoriteAnalysisMutationVariables,
} from '../../utils/apollo/graphqlSchema';
import { FAVORITE_ANALYSIS } from '../../utils/apollo/favorite/mutations';
import printGraphqlErrors from '../../utils/apollo/printGraphqlErrors';
import toastMessage from '../../utils/toastMessage';
import { FavoriteStarIcon, IconWrapper } from '../Icon';
import { colors } from '../../config/styles';
import { isScratchpadAnalysisId } from '../../redux/utils/transientAnalysis';

interface IProps {
  analysisId: string;
  isFavorite: boolean;
  analysisDisplay: string;
  onToggle?: () => void;
  isDisabled?: boolean;
}

const FavoriteAnalysis = ({
  analysisId,
  analysisDisplay,
  isFavorite,
  onToggle,
  isDisabled,
}: IProps) => {
  const [setFavorite] = useMutation<
    IFavoriteAnalysisMutation,
    IFavoriteAnalysisMutationVariables
  >(FAVORITE_ANALYSIS);

  const isScratchpadAnalysis = isScratchpadAnalysisId(analysisId);

  const toggleFavorite = useCallback(
    async (e: React.MouseEvent) => {
      if (isDisabled) {
        return;
      }
      e.stopPropagation();
      const res = await setFavorite({
        variables: {
          input: {
            analysisId,
            isFavorite: !isFavorite,
          },
        },
      });
      const mutationRes = res?.data?.favoriteAnalysis;
      if (printGraphqlErrors(mutationRes?.errors)) {
        return;
      }
      const actionText = isFavorite ? 'removed from' : 'added to';
      toastMessage({
        message: `${analysisDisplay} has been ${actionText} your favorites.`,
        type: 'info',
      });

      if (onToggle) {
        onToggle();
      }
    },
    [
      analysisDisplay,
      analysisId,
      isDisabled,
      isFavorite,
      onToggle,
      setFavorite,
    ],
  );

  if (isScratchpadAnalysis) {
    return <FavoriteStarIcon isActive={isFavorite} />;
  }

  return (
    <IconWrapper
      size={24}
      onClick={toggleFavorite}
      hoverColor={colors.favoriteGoldTitle}
      isDisabled={isDisabled}
    >
      <FavoriteStarIcon isActive={isFavorite} />
    </IconWrapper>
  );
};

export default FavoriteAnalysis;
